import Phaser from 'phaser';
import { Enemy, EnemyType } from '../entities/Enemy';
import { Boss } from '../entities/Boss';
import { Player } from '../entities/Player';
import { GameConfig } from '../config/GameConfig';

export class EnemySpawner {
  private scene: Phaser.Scene;
  private player: Player;
  private enemies: Phaser.Physics.Arcade.Group;
  private spawnTimer?: Phaser.Time.TimerEvent;
  private bossTimer?: Phaser.Time.TimerEvent;
  private waveNumber: number = 1;

  constructor(scene: Phaser.Scene, player: Player) {
    this.scene = scene;
    this.player = player;

    this.enemies = scene.physics.add.group({
      runChildUpdate: true
    });

    this.startSpawning();
    this.startBossSpawning();
  }

  private startSpawning(): void {
    this.spawnTimer = this.scene.time.addEvent({
      delay: GameConfig.ENEMY.SPAWN_INTERVAL,
      callback: this.spawnWave,
      callbackScope: this,
      loop: true
    });
  }

  private startBossSpawning(): void {
    // Spawn first boss after 60 seconds, then every 60 seconds (optimized performance)
    this.scene.time.delayedCall(60000, () => {
      this.bossTimer = this.scene.time.addEvent({
        delay: 60000,
        callback: this.spawnBoss,
        callbackScope: this,
        loop: true
      });
      this.spawnBoss();
    });
  }

  private spawnWave(): void {
    // Cap total active enemies for performance
    const activeEnemies = this.enemies.getChildren().filter((e: any) => e.active).length;
    if (activeEnemies > 45) {
      return; // Don't spawn if too many enemies
    }

    // Progressive difficulty: more enemies over time
    const baseEnemies = 2;
    const numEnemies = Math.min(
      baseEnemies + Math.floor(this.waveNumber / 4),
      5 // Hard cap at 5 per wave
    );

    for (let i = 0; i < numEnemies; i++) {
      this.spawnEnemy();
    }

    this.waveNumber++;
  }

  private getEnemyType(): EnemyType {
    // Progressive difficulty: introduce new types over time
    const rand = Math.random() * 100;

    // Early game (waves 1-5): mostly normal
    if (this.waveNumber < 5) {
      if (rand < 70) return 'normal';
      if (rand < 90) return 'fast';
      return 'dasher';
    }

    // Mid game (waves 5-10): introduce more variety
    if (this.waveNumber < 10) {
      if (rand < 35) return 'normal';
      if (rand < 60) return 'fast';
      if (rand < 75) return 'dasher';
      if (rand < 90) return 'splitter';
      return 'tank';
    }

    // Late game (waves 10-20): all types appear
    if (this.waveNumber < 20) {
      if (rand < 20) return 'normal';
      if (rand < 35) return 'fast';
      if (rand < 50) return 'dasher';
      if (rand < 65) return 'splitter';
      if (rand < 80) return 'ranged';
      if (rand < 92) return 'tank';
      return 'elite';
    }

    // End game (20+): more challenging enemies
    if (rand < 15) return 'normal';
    if (rand < 25) return 'fast';
    if (rand < 40) return 'dasher';
    if (rand < 55) return 'splitter';
    if (rand < 70) return 'ranged';
    if (rand < 85) return 'tank';
    return 'elite';
  }

  private spawnEnemy(): void {
    const { x, y } = this.getSpawnPosition();
    const type = this.getEnemyType();
    const enemy = new Enemy(this.scene, x, y, this.player, type);
    this.enemies.add(enemy);
  }

  private spawnBoss(): void {
    const { x, y } = this.getSpawnPosition();
    const boss = new Boss(this.scene, x, y, this.player);
    this.enemies.add(boss);
  }

  private getSpawnPosition(): { x: number; y: number } {
    const camera = this.scene.cameras.main;
    const spawnDistance = 100;

    // Spawn outside camera view
    const side = Phaser.Math.Between(0, 3);
    let x = 0;
    let y = 0;

    switch (side) {
      case 0: // Top
        x = Phaser.Math.Between(camera.scrollX, camera.scrollX + camera.width);
        y = camera.scrollY - spawnDistance;
        break;
      case 1: // Right
        x = camera.scrollX + camera.width + spawnDistance;
        y = Phaser.Math.Between(camera.scrollY, camera.scrollY + camera.height);
        break;
      case 2: // Bottom
        x = Phaser.Math.Between(camera.scrollX, camera.scrollX + camera.width);
        y = camera.scrollY + camera.height + spawnDistance;
        break;
      case 3: // Left
        x = camera.scrollX - spawnDistance;
        y = Phaser.Math.Between(camera.scrollY, camera.scrollY + camera.height);
        break;
    }

    return { x, y };
  }

  public getEnemies(): Phaser.Physics.Arcade.Group {
    return this.enemies;
  }

  public destroy(): void {
    if (this.spawnTimer) {
      this.spawnTimer.destroy();
    }
    if (this.bossTimer) {
      this.bossTimer.destroy();
    }
    this.enemies.clear(true, true);
  }
}
